import { useMemo } from 'react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { StoneXGrid } from '../../../StoneX';
import { StoneXNumber } from '../../../StoneXMui';
import { MatrixGraphPoint, MatrixTermResult, MatrixYearPriceSeries } from './MatrixTypes';

const TERM_ORDER: string[] = ['Annual', 'Summer', 'Winter', 'Q1', 'Q2', 'Q3', 'Q4', 'January-February', 'March-April', 'July-August', 'October-December'];

interface MatrixSpotRateComparisonTableProps {
  graphPoints: MatrixGraphPoint[];
  yearPriceSeries: MatrixYearPriceSeries[];
  results: MatrixTermResult[];
  selectedPercentiles: number[];
}

function getDecileValue(deciles: { rank: number; value: number }[], rank: number): number | null {
  const decile = deciles.find(d => Math.abs(d.rank - rank) < 0.001);
  return decile?.value ?? null;
}

interface AltViewRow {
  label: string;
  close: number | null;
  average: number | null;
  isSummary?: boolean;
  [key: string]: any;
}

function getAverageValue(values: Array<number | null | undefined>): number | null {
  const numericValues = values.filter((value): value is number => value != null);

  if (numericValues.length === 0) {
    return null;
  }

  const total = numericValues.reduce((sum, value) => sum + value, 0);
  return total / numericValues.length;
}

function getTermSortIndex(term: string): number {
  const index = TERM_ORDER.indexOf(term);
  return index >= 0 ? index : TERM_ORDER.length;
}

interface TermTableData {
  termLabel: string;
  rows: AltViewRow[];
  summaryRow: AltViewRow | null;
}

export default function MatrixSpotRateComparisonTable({ graphPoints, yearPriceSeries, results, selectedPercentiles }: MatrixSpotRateComparisonTableProps) {
  const sortedDeciles = useMemo(() => [...selectedPercentiles].sort((a, b) => b - a), [selectedPercentiles]);

  const termTables = useMemo((): TermTableData[] => {
    if (yearPriceSeries.length > 0) {
      // History mode: group yearPriceSeries by term, each gets its own table with its own deciles
      const lastPoint = graphPoints.length > 0 ? graphPoints[graphPoints.length - 1] : null;
      const calculationDate = lastPoint?.calculationDate ?? null;

      // Build a map of term name -> MatrixTermResult from the last graph point
      const termResultMap = new Map<string, MatrixTermResult>();
      if (lastPoint) {
        for (const term of lastPoint.terms) {
          termResultMap.set(term.season, term);
        }
      }
      // Fallback to results
      if (termResultMap.size === 0) {
        for (const term of results) {
          termResultMap.set(term.season, term);
        }
      }

      // Group yearPriceSeries by term
      const seriesByTerm = new Map<string, MatrixYearPriceSeries[]>();
      for (const series of yearPriceSeries) {
        const termKey = series.term || 'Annual';
        if (!seriesByTerm.has(termKey)) {
          seriesByTerm.set(termKey, []);
        }
        seriesByTerm.get(termKey)!.push(series);
      }

      // Build a table for each term, sorted by TERM_ORDER
      const termKeys = Array.from(seriesByTerm.keys()).sort((a, b) => getTermSortIndex(a) - getTermSortIndex(b));

      return termKeys.map(termKey => {
        const termResult = termResultMap.get(termKey) ?? null;
        const termSeries = seriesByTerm.get(termKey)!;

        // Build summary row from this term's deciles
        let summaryRow: AltViewRow | null = null;
        if (termResult) {
          const mean = termResult.weightedMean;
          const summaryDecileValues = sortedDeciles.map((decile) => getDecileValue(termResult.weightedDeciles, decile / 100));
          const summary: AltViewRow = {
            label: calculationDate ? `Deciles (${dayjs(calculationDate).format('MM/DD/YYYY')})` : 'Deciles',
            close: mean,
            average: getAverageValue(summaryDecileValues),
            isSummary: true,
          };
          for (const decile of sortedDeciles) {
            summary[`p${decile}`] = getDecileValue(termResult.weightedDeciles, decile / 100);
          }
          summaryRow = summary;
        }

        // Build data rows — one per year
        const dataRows: AltViewRow[] = termSeries.map(yearSeries => {
          const dailyPrices = yearSeries.dailyPrices;
          const latestPrice = dailyPrices.length > 0 ? dailyPrices[dailyPrices.length - 1].price : null;
          const decileValues = termResult
            ? sortedDeciles.map((decile) => getDecileValue(termResult.weightedDeciles, decile / 100))
            : sortedDeciles.map(() => null);
          const average = latestPrice != null
            ? getAverageValue(decileValues.map((decileValue) => (decileValue != null ? latestPrice - decileValue : null)))
            : null;

          const row: AltViewRow = {
            label: `${yearSeries.year}`,
            close: latestPrice,
            average,
          };

          for (let index = 0; index < sortedDeciles.length; index++) {
            const decile = sortedDeciles[index];
            const decileValue = decileValues[index];
            row[`p${decile}`] = latestPrice != null && decileValue != null ? latestPrice - decileValue : null;
          }

          return row;
        });

        return { termLabel: termKey, rows: dataRows, summaryRow };
      });
    } else if (results.length > 0) {
      // Single-day mode: single table, one row per term
      const sortedResults = [...results].sort((a, b) => getTermSortIndex(a.season) - getTermSortIndex(b.season));
      const lastPoint = graphPoints.length > 0 ? graphPoints[graphPoints.length - 1] : null;
      const calculationDate = lastPoint?.calculationDate ?? null;

      // Use first term for the summary (single-day has one shared set of deciles displayed)
      const termResult = sortedResults[0];
      const mean = termResult.weightedMean;
      const summaryDecileValues = sortedDeciles.map((decile) => getDecileValue(termResult.weightedDeciles, decile / 100));
      const summary: AltViewRow = {
        label: calculationDate ? `Deciles (${dayjs(calculationDate).format('MM/DD/YYYY')})` : 'Deciles',
        close: mean,
        average: getAverageValue(summaryDecileValues),
        isSummary: true,
      };
      for (const decile of sortedDeciles) {
        summary[`p${decile}`] = getDecileValue(termResult.weightedDeciles, decile / 100);
      }

      const dataRows: AltViewRow[] = sortedResults.map(term => {
        const prices = term.outerPrices;
        const latestPrice = prices.length > 0 ? prices[prices.length - 1].price : null;
        const decileValues = sortedDeciles.map((decile) => getDecileValue(term.weightedDeciles, decile / 100));
        const average = latestPrice != null
          ? getAverageValue(decileValues.map((decileValue) => (decileValue != null ? latestPrice - decileValue : null)))
          : null;

        const row: AltViewRow = {
          label: term.season,
          close: latestPrice,
          average,
        };

        for (let index = 0; index < sortedDeciles.length; index++) {
          const decile = sortedDeciles[index];
          const decileValue = decileValues[index];
          row[`p${decile}`] = latestPrice != null && decileValue != null ? latestPrice - decileValue : null;
        }

        return row;
      });

      return [{ termLabel: '', rows: dataRows, summaryRow: summary }];
    }

    return [];
  }, [graphPoints, yearPriceSeries, results, sortedDeciles]);

  const columns = useMemo<ColDef<AltViewRow>[]>(() => {
    const numberRenderer = (params: ICellRendererParams<AltViewRow>) => {
      const value = params.value as number | null;
      if (value == null) { return <span></span>; }
      if (params.data?.isSummary) {
        return <StoneXNumber number={value} decimals={4} />;
      }
      return <StoneXNumber number={value} decimals={4} colored flipped />;
    };

    const decileRenderer = (params: ICellRendererParams<AltViewRow>) => {
      const value = params.value as number | null;
      if (value == null) { return <span></span>; }
      if (params.data?.isSummary) {
        return <StoneXNumber number={value} decimals={4} />;
      }
      const close = params.data?.close;
      const shouldColor = value < 0 || (close != null && close !== 0 && value / close <= 0.05);
      return <StoneXNumber number={value} decimals={4} colored={shouldColor} flipped />;
    };

    const cols: ColDef<AltViewRow>[] = [
      { headerName: 'Term', field: 'label', flex: 1.2 },
      {
        headerName: 'Close',
        field: 'close',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<AltViewRow>) => {
          const value = params.value as number | null;
          if (value == null) { return <span></span>; }
          return <StoneXNumber number={value} decimals={4} />;
        },
      },
      { headerName: 'Average', field: 'average', flex: 1, cellRenderer: numberRenderer },
    ];

    for (const decile of sortedDeciles) {
      cols.push({
        headerName: `${decile}th`,
        field: `p${decile}`,
        flex: 1,
        cellRenderer: decileRenderer,
      });
    }

    return cols;
  }, [sortedDeciles]);

  if (termTables.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {termTables.map(({ termLabel, rows, summaryRow }) => (
        <div key={termLabel || 'single'}>
          {termLabel && <h4 style={{ margin: '0 0 4px 0' }}>{termLabel}</h4>}
          <StoneXGrid
            rows={rows}
            columns={columns}
            hideGridOptions
            gridOptions={{ pinnedTopRowData: summaryRow ? [summaryRow] : [] }}
          />
        </div>
      ))}
    </div>
  );
}
